import type { SavedViewList } from '@cyberscore/contracts';
import { Bookmark, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { hashQuery, navigate, useHash } from '../../lib/router';
import { Menu } from '../../ui/Menu';
import { useToast } from '../../ui/Toasts';
import { useSavedViewChanges, useSavedViews } from './api';

/** The list's query as a view keeps it: filters, search and sort, not the page or the card/table choice. */
const viewQuery = (hash: string) => {
  const q = hashQuery(hash);
  q.delete('page');
  q.delete('view');
  return q.toString();
};

/**
 * Saved views of a list (task 66): a name for its filters, search and sort, kept for the person or shared
 * with their organisation. Picking one opens the list with them.
 */
export function SavedViews({ list, base }: { list: SavedViewList; base: string }) {
  const views = useSavedViews(list);
  const { create, remove } = useSavedViewChanges(list);
  const toast = useToast();
  const hash = useHash();
  const current = viewQuery(hash);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = views.data?.find((v) => v.query === current && current);

  const save = async () => {
    setError(null);
    try {
      await create.mutateAsync({ name: name.trim(), query: current, shared });
      toast(`Saved the view ${name.trim()}.`);
      setName('');
      setShared(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the view');
    }
  };

  return (
    <Menu label={active ? active.name : 'Views'} icon={<Bookmark size={15} />} align="right" active={!!active}>
      {(close) => (
        <div className="views-menu">
          <div className="menu-label">Saved views</div>
          {views.data?.length ? (
            views.data.map((v) => (
              <div key={v.id} className="views-row">
                <button type="button" role="menuitem" className="menu-item" onClick={() => { navigate(`${base}${v.query ? `?${v.query}` : ''}`); close(); }}>
                  <span className="check">{v.id === active?.id ? '✓' : ''}</span>
                  <span className="grow">{v.name}</span>
                  {v.shared && <Users size={13} aria-label={v.mine ? 'Shared' : `Shared by ${v.created_by_name ?? 'someone'}`} />}
                </button>
                {v.mine && (
                  <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Delete view ${v.name}`} onClick={() => remove.mutate(v.id)}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))
          ) : (
            <p className="muted small views-none">None yet. Filter and sort the list, then save it here.</p>
          )}
          <div className="menu-sep" />
          <form className="views-save" onSubmit={(e) => { e.preventDefault(); if (name.trim()) void save(); }}>
            <label className="menu-label" htmlFor={`view-name-${list}`}>Save this view</label>
            <input id={`view-name-${list}`} className="input" placeholder="e.g. Renewing this month" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
            <label className="small views-share">
              <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /> Share with my organisation
            </label>
            {error && <span className="error small">{error}</span>}
            <button type="submit" className="btn btn-sm btn-primary" disabled={!name.trim() || create.isPending}>Save view</button>
          </form>
        </div>
      )}
    </Menu>
  ); // prettier-ignore
}
